import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { arrayToObject } from 'lib/array/arrayUtils'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MerchandiseSearchFilter from './MerchandiseSearchFilter'
import { EmptyObject, omitKeys } from 'lib/object/objectUtils'
import { capitalise } from 'lib/string/stringUtils'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { semiFormToObject } from 'lib/forms/formUtils'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import noop from 'lib/function/noop'
import { setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { useLocation } from 'react-router'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replaceWithRegion } from 'actions/NavigationActions'
import debounce from 'lodash.debounce'
import MerchandiseSearchPageFiltersSkeleton from './MerchandiseSearchPageFiltersSkeleton'
import useMerchandiseSearchFacets from 'hooks/Merchandise/useMerchandiseSearchFacets'
import useLastNonEmptyValue from 'hooks/useLastNonEmptyValue'
import { calculateMerchandiseFilterCount, mapMerchandiseFilterFormToFilters, getUrlCategoryIds, mergeFiltersWithPreservedData } from './merchandiseSearchPageUtils'

const filterTitleMap = new Map<App.MerchandiseSearchFacetFilterValue, string>([
  ['brands', 'Brands'],
  ['categories', 'Categories'],
  ['points', `Price (${getLoyaltyProgramDisplayName()} Points)`],
  ['price', 'Price'],
  ['freeShipping', 'Shipping'],
  ['earnRatio', 'Earn bonus points'],
  ['size', 'Size'],
  ['colour', 'Colour'],
  ['color', 'Colour'],
  ['material', 'Material'],
  ['campaigns', 'Sale & Offers'],
  ['tags', 'Collections'],
])

export type MerchandiseFilterFacetForm = {
  brands?: string | Array<string>
  campaigns?: string | Array<string>
  categories?: string | Array<string>
  tags?: string | Array<string>
  points?: { start: number, end: number }
  price?: { start: number, end: number }
  sortBy?: App.MerchandiseSearchSort
  freeShipping?: string | Array<string>
  earnRatio?: string | Array<string>
} & Record<string, string | Array<string>>

interface Props {
  /**
   * These are filters the user has selected
   */
  userFilters?: App.MerchandiseFilters
  /**
   * Category context (from URL path)
   */
  category?: App.MerchandiseCategory
  /**
   * Filters from URL query params (for calculating what came from path vs query)
   */
  urlFilters?: App.MerchandiseFilters
  /**
   * Callback for filter changes
   * - Desktop: Not provided, component updates URL directly
   * - Modal: Provided, component notifies parent of changes
   */
  onChange?: (filters: MerchandiseFilterFacetForm, mergedFilters?: App.MerchandiseFilters) => void
  /**
   * Callback for reset action
   */
  onReset?: () => void
  /**
   * Show "Reset all" button with filter count (desktop only)
   */
  showResetButton?: boolean
}

function MerchandiseSearchFilters(props: Props) {
  const { userFilters = EmptyObject, category, urlFilters = EmptyObject, onChange, onReset = noop, showResetButton = false }: Props = props
  const location = useLocation()
  const dispatch = useAppDispatch()

  const containerRef = useRef<HTMLDivElement>(null)
  const [resetKey, setResetKey] = useState(0)

  // Determine if we're in modal mode (has onChange callback)
  const isModalMode = !!onChange

  // Track local filter state for progressive filtering in modal
  const [localFilters, setLocalFilters] = useState<App.MerchandiseFilters>(userFilters)

  // Sync local filters when userFilters change (e.g., modal reopens)
  useEffect(() => {
    setLocalFilters(userFilters)
  }, [userFilters])

  // Calculate URL-based category IDs (category from path, not query params)
  const urlCategoryIds = useMemo(
    () => getUrlCategoryIds(category, urlFilters),
    [category, urlFilters],
  )

  // In modal mode: use localFilters (progressive preview state)
  // In desktop mode: use userFilters (URL state)
  const filtersForSelection = isModalMode ? localFilters : userFilters

  const facetApiFilters = useMemo((): App.MerchandiseFilters => {
    // For facet API calls, send only the most specific (child-level) category:
    // Priority: 1) Query param categories, 2) URL subcategory, 3) URL parent category
    const filterCategories = filtersForSelection.categories ?? []

    let categoriesToSend: Array<string>
    if (filterCategories.length > 0) {
      // User-selected categories from query params take priority
      categoriesToSend = filterCategories
    } else if (urlCategoryIds.length > 0) {
      // Use only the most specific URL-path category (first element = subcategory)
      // urlCategoryIds is [categoryId, ...ancestorIds], so [0] is most specific
      categoriesToSend = [urlCategoryIds[0]]
    } else {
      categoriesToSend = []
    }

    return {
      ...filtersForSelection,
      categories: categoriesToSend,
    }
  }, [filtersForSelection, urlCategoryIds])

  // Fetch facets progressively as filters change (both modal and desktop)
  const [fetchedFacets, fetching] = useMerchandiseSearchFacets(facetApiFilters)

  // Maintain facet cache to avoid flickering
  const cachedFacets = useLastNonEmptyValue(fetchedFacets) ?? fetchedFacets

  const showSkeleton = fetching && cachedFacets.length === 0

  // Calculate filter count (always use URL filters as source of truth)
  const filterCount = useMemo(
    () => calculateMerchandiseFilterCount(userFilters, category, urlFilters),
    [userFilters, category, urlFilters],
  )

  const previousFilterCountRef = useRef(filterCount)

  const selectedOptions = useMemo<Record<string, Array<string> | { min?: number, max?: number } | undefined>>(() => {
    const optionFilters = arrayToObject(
      filtersForSelection.options ?? [],
      filter => filter.type,
      filter => filter.value,
    )
    return {
      ...omitKeys(['name', 'options', 'sortBy'], filtersForSelection),
      ...optionFilters,
    }
  }, [filtersForSelection])

  // Force component remount when filters are cleared (to reset uncontrolled inputs like DualSliderInput)
  useEffect(() => {
    // Detect when filters went from having some to having none
    if (previousFilterCountRef.current > 0 && filterCount === 0) {
      // Increment reset key to force remount of all filter components
      setResetKey(prev => prev + 1)
    }
    previousFilterCountRef.current = filterCount
  }, [filterCount])

  const onFilterChange = useCallback(() => {
    if (!containerRef.current) return

    const formValues = semiFormToObject<MerchandiseFilterFacetForm>(containerRef.current)

    // Preserve existing price/points range when not present in form (since we commit via onRangeCommit)
    const nextValues: MerchandiseFilterFacetForm = { ...formValues }
    if (!nextValues.price && (filtersForSelection.price?.min !== undefined || filtersForSelection.price?.max !== undefined)) {
      nextValues.price = {
        start: filtersForSelection.price?.min as number,
        end: filtersForSelection.price?.max as number,
      }
    }
    if (!nextValues.points && (filtersForSelection.points?.min !== undefined || filtersForSelection.points?.max !== undefined)) {
      nextValues.points = {
        start: filtersForSelection.points?.min as number,
        end: filtersForSelection.points?.max as number,
      }
    }
    // In modal mode: update local state and notify parent
    if (isModalMode) {
      const mappedFilters = mapMerchandiseFilterFormToFilters(nextValues)
      const mergedFilters = mergeFiltersWithPreservedData(mappedFilters, userFilters, urlCategoryIds)

      setLocalFilters(mergedFilters) // Update local state for progressive facet fetching
      onChange?.(nextValues, mergedFilters)
    }
    // Desktop mode: onChange is undefined, no action needed
  }, [onChange, filtersForSelection, isModalMode, userFilters, urlCategoryIds])

  const onRangeCommit = useMemo(() => {
    const debouncedFn = debounce((filter: string, start?: number, end?: number) => {
      if (isModalMode) {
        // Modal mode: update local state and notify parent
        if (!containerRef.current) return

        const formValues = semiFormToObject<MerchandiseFilterFacetForm>(containerRef.current)

        // Add the range values to the form data
        const nextValues = { ...formValues } as MerchandiseFilterFacetForm

        // Find the facet to get boundary values
        const facet = cachedFacets.find(f => f.filter === filter)
        const facetMin = facet?.type === 'range' ? facet.min : 0
        const facetMax = facet?.type === 'range' ? facet.max : Number.MAX_SAFE_INTEGER

        if (start !== undefined || end !== undefined) {
          // At least one value is not at boundary
          nextValues[filter as 'price' | 'points'] = {
            start: start ?? facetMin, // Use facet min if at boundary
            end: end ?? facetMax, // Use facet max if at boundary
          }
        } else {
          // Both at boundaries → clear the filter from form values
          delete nextValues[filter as 'price' | 'points']
        }

        // Map filters and update local state for progressive facet fetching
        const mappedFilters = mapMerchandiseFilterFormToFilters(nextValues)
        const mergedFilters = mergeFiltersWithPreservedData(mappedFilters, userFilters, urlCategoryIds)

        setLocalFilters(mergedFilters)
        onChange?.(nextValues, mergedFilters)
      } else {
        // Desktop mode: update URL directly
        const params: Array<{ paramName: string, paramValue: number | undefined }> = []
        const bothAtBoundary = start === undefined && end === undefined

        // Update min: set value if provided, clear if both at boundary
        if (start !== undefined || bothAtBoundary) {
          params.push({ paramName: `${filter}.min`, paramValue: start })
        }

        // Update max: set value if provided, clear if both at boundary
        if (end !== undefined || bothAtBoundary) {
          params.push({ paramName: `${filter}.max`, paramValue: end })
        }

        const nextSearch = setManySearchParamValues(window.location.search, params)
        dispatch(replaceWithRegion(location.pathname, nextSearch))
      }
    }, 50)

    return debouncedFn
  }, [dispatch, location.pathname, isModalMode, onChange, cachedFacets, userFilters, urlCategoryIds])

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      onRangeCommit.cancel()
    }
  }, [onRangeCommit])

  return <VerticalSpacer gap={24}>
    {showResetButton && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <Heading variant="heading4">Filters</Heading>
      {filterCount > 0 && (
        <TextLink onClick={onReset}>
          Reset all ({filterCount})
        </TextLink>
      )}
    </Group>}
    <div ref={containerRef}>
      <VerticalSpacer gap={24}>
        {showSkeleton && <MerchandiseSearchPageFiltersSkeleton />}
        {!showSkeleton && cachedFacets.map((facet) => <FilterPanelFilterGroup
          key={facet.filter}
          title={filterTitleMap.get(facet.filter) ?? capitalise(facet.filter)}
        >
          <MerchandiseSearchFilter
            key={`${facet.filter}-${resetKey}`}
            facet={facet}
            defaultSelected={selectedOptions[facet.filter]}
            onChange={onFilterChange}
            onRangeCommit={onRangeCommit}
          />
        </FilterPanelFilterGroup>)}
      </VerticalSpacer>
    </div>
  </VerticalSpacer>
}

export default MerchandiseSearchFilters
